import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Paper,
    CircularProgress,
    Alert,
    InputAdornment,
    Grid,
    Skeleton
} from "@mui/material";
import { CheckCircle, User, Mail, Phone, Calendar, Building2, PhoneCall } from "lucide-react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../services/axiosInstance";


export default function LeadCaptureForm() {
    const { userId } = useParams();
    const [companyInfo, setCompanyInfo] = useState({ companyName: "", did: "" });
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        date: "",
        message: "",
    });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [errorMessage, setErrorMessage] = useState("");

    // Fetch company info on mount
    useEffect(() => {
        const fetchCompanyInfo = async () => {
            try {
                const res = await axiosInstance.get(`/leads/public/user/${userId}`);
                if (res.data.success) {
                    setCompanyInfo(res.data.data);
                }
            } catch (error) {
                console.error("Error fetching company info:", error);
            } finally {
                setLoading(false);
            }
        };
        if (userId) fetchCompanyInfo();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (errors[name]) setErrors({ ...errors, [name]: "" });
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Full name is required";
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Valid email is required";
        if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
        if (!formData.date) newErrors.date = "Select a date";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setStatus("submitting");
        try {
            const res = await axiosInstance.post(`/leads/public/submit`, {
                userId, ...formData, closingDate: formData.date
            });
            if (res.data.success) setStatus("success");
        } catch (error) {
            setErrorMessage("Failed to submit. Please try again.");
            setStatus("error");
        }
    };

    const today = new Date().toISOString().split('T')[0];

    if (status === "success") {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#F8FAFC" }}>
                <Paper elevation={0} sx={{ p: 6, textAlign: "center", maxWidth: 500, borderRadius: 4, border: "1px solid #E2E8F0" }}>
                    <Box sx={{ mb: 3, display: "inline-flex", p: 2, borderRadius: "50%", bgcolor: "#F0FDF4" }}>
                        <CheckCircle size={48} color="#22C55E" />
                    </Box>
                    <Typography variant="h4" fontWeight="800" gutterBottom sx={{ color: "#1E293B" }}>Success!</Typography>
                    <Typography color="text.secondary" sx={{ mb: 4 }}>
                        We've received your inquiry. The team at <strong>{companyInfo.companyName}</strong> will contact you shortly.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setFormData({ name: "", email: "", phone: "", date: "", message: "" });
                            setErrors({});
                            setErrorMessage("");
                            setStatus("idle");
                        }}
                        sx={{ borderRadius: 2, px: 4 }}
                    >
                        Submit Another
                    </Button>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", py: 8, px: 2 }}>
            <Container maxWidth="md">
                <Paper elevation={0} sx={{ display: "flex", borderRadius: 4, overflow: "hidden", border: "1px solid #E2E8F0", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.05)" }}>
                    <Grid container>
                        {/* Sidebar/Info Section */}
                        <Grid item xs={12} md={4} sx={{ bgcolor: "#1E293B", p: 4, color: "white", display: "flex", flexDirection: "column" }}>
                            {loading ? (
                                <>
                                    <Skeleton variant="text" width="70%" height={40} sx={{ bgcolor: "rgba(255,255,255,0.1)" }} />
                                    <Skeleton variant="text" width="90%" height={20} sx={{ bgcolor: "rgba(255,255,255,0.1)", mt: 2 }} />
                                </>
                            ) : (
                                <>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                        <Building2 size={22} />
                                        <Typography variant="h6" fontWeight="700">
                                            {companyInfo.companyName}
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ opacity: 0.8, mb: 4 }}>
                                        Connect with us directly for your personalized consultation.
                                    </Typography>
                                </>
                            )}

                            <Box sx={{ mt: "auto" }}>
                                {companyInfo.did && (
                                    <>
                                        <Typography variant="body2" sx={{ opacity: 0.6, mb: 1 }}>Need instant help?</Typography>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <PhoneCall size={18} />
                                            <Typography variant="body1" fontWeight="600">
                                                0{companyInfo.did}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Grid>

                        {/* Form Section */}
                        <Grid item xs={12} md={8} sx={{ p: { xs: 3, md: 5 }, bgcolor: "white" }}>
                            {loading ? (
                                <Skeleton variant="text" width="60%" height={40} />
                            ) : (
                                <Typography variant="h5" fontWeight="800" sx={{ color: "#0F172A", mb: 1 }}>
                                    Schedule an Appointment
                                </Typography>
                            )}
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 4 }}>
                                Fill in the details below to lock in your preferred time.
                            </Typography>

                            {status === "error" && <Alert severity="error" sx={{ mb: 3 }}>{errorMessage}</Alert>}

                            <Box component="form" onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Full Name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            error={!!errors.name}
                                            helperText={errors.name}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><User size={18} /></InputAdornment>) }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            error={!!errors.email}
                                            helperText={errors.email}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><Mail size={18} /></InputAdornment>) }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            error={!!errors.phone}
                                            helperText={errors.phone}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><Phone size={18} /></InputAdornment>) }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Preferred Date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            error={!!errors.date}
                                            helperText={errors.date}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ min: today }}
                                            InputProps={{ startAdornment: (<InputAdornment position="start"><Calendar size={18} /></InputAdornment>) }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Message"
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            placeholder="Any specific requirements?"
                                        />
                                    </Grid>
                                </Grid>

                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={status === "submitting" || loading}
                                    sx={{ mt: 4, py: 1.8, bgcolor: "#2563EB", fontWeight: 700, borderRadius: 2, boxShadow: "none", textTransform: "none", "&:hover": { bgcolor: "#1D4ED8" } }}
                                >
                                    {status === "submitting" ? <CircularProgress size={24} color="inherit" /> : "Confirm Booking"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Container>
        </Box>
    );
}